import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LanguageMultiCombobox } from "@/components/ui/language-multi-combobox";
import { apiSend } from "@/lib/api-error";
import { stripBosnian } from "@/lib/languages";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { Panel } from "./detail/Panel";
import { FloppyDisk, CircleNotch } from "@phosphor-icons/react";
import type { Therapist, PayoutMethod } from "@/types";

const PAYOUT_OPTIONS: Array<{ value: PayoutMethod; label: string; hint: string }> = [
  { value: "invoice", label: "Faktura", hint: "Ima registrovanu firmu / obrt" },
  { value: "work_contract", label: "Ugovor o djelu", hint: "Naplata na lične podatke" },
];

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="block text-xs font-medium text-muted-foreground">{children}</span>
  );
}

/**
 * Admin-editable flags, compact enough for the narrow rail. This card is the
 * single home for volunteer status, payout method and languages, the facts
 * panels above intentionally do not repeat them.
 */
export function TherapistFlagsCard({ therapist }: { therapist: Therapist }) {
  const queryClient = useQueryClient();
  const [isVolunteer, setIsVolunteer] = useState(Boolean(therapist.isVolunteer));
  const [payoutMethod, setPayoutMethod] = useState<PayoutMethod>(therapist.payoutMethod);
  const [languages, setLanguages] = useState<string[]>(stripBosnian(therapist.languages));

  useEffect(() => {
    setIsVolunteer(Boolean(therapist.isVolunteer));
    setPayoutMethod(therapist.payoutMethod);
    setLanguages(stripBosnian(therapist.languages));
  }, [therapist.id, therapist.isVolunteer, therapist.payoutMethod, therapist.languages]);

  const dirty =
    isVolunteer !== Boolean(therapist.isVolunteer) ||
    payoutMethod !== therapist.payoutMethod ||
    !arraysEqual(languages, stripBosnian(therapist.languages));

  const mutation = useMutation({
    mutationFn: async () =>
      apiSend(
        `/api/therapists/${therapist.id}`,
        "PATCH",
        { isVolunteer, payoutMethod, languages },
        "Greška pri snimanju oznaka"
      ),
    onSuccess: () => {
      toast.success("Oznake spremljene");
      queryClient.invalidateQueries({ queryKey: ["admin", "therapist", therapist.id] });
      queryClient.invalidateQueries({ queryKey: ["admin", "therapists"] });
    },
  });

  return (
    <Panel
      title="Oznake"
      action={
        <Button
          size="sm"
          onClick={() => mutation.mutate()}
          disabled={!dirty || mutation.isPending}
        >
          {mutation.isPending ? (
            <CircleNotch className="size-3.5 animate-spin" />
          ) : (
            <FloppyDisk className="size-3.5" />
          )}
          Spremi
        </Button>
      }
    >
      <div className="space-y-4">
        <label className="flex cursor-pointer items-start gap-2.5">
          <Checkbox
            className="mt-0.5"
            checked={isVolunteer}
            onCheckedChange={(c) => setIsVolunteer(c === true)}
          />
          <span className="min-w-0">
            <span className="block text-sm font-medium text-foreground">Volonter</span>
            <span className="block text-xs text-muted-foreground">
              Javna oznaka na profilu terapeuta.
            </span>
          </span>
        </label>

        <div>
          <GroupLabel>Način naplate pretplate</GroupLabel>
          <div className="mt-1.5 space-y-1.5" role="radiogroup" aria-label="Način naplate pretplate">
            {PAYOUT_OPTIONS.map((option) => {
              const checked = payoutMethod === option.value;
              return (
                <label
                  key={option.value}
                  className={cn(
                    "flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2 transition-colors",
                    checked
                      ? "border-primary/50 bg-primary/5"
                      : "border-border hover:border-primary/30"
                  )}
                >
                  <input
                    type="radio"
                    name="payout-method"
                    className="peer sr-only"
                    checked={checked}
                    disabled={mutation.isPending}
                    onChange={() => setPayoutMethod(option.value)}
                  />
                  <span
                    aria-hidden
                    className={cn(
                      "mt-1 grid size-3.5 shrink-0 place-items-center rounded-full border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 peer-focus-visible:ring-offset-1",
                      checked ? "border-primary" : "border-muted-foreground/40"
                    )}
                  >
                    {checked && <span className="size-1.5 rounded-full bg-primary" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">
                      {option.label}
                    </span>
                    <span className="block text-xs text-muted-foreground">{option.hint}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <GroupLabel>Jezici</GroupLabel>
          <p className="mt-0.5 text-xs text-muted-foreground/70">
            Bosanski je uključen automatski. Najviše 5 dodatnih jezika.
          </p>
          <div className="mt-1.5">
            <LanguageMultiCombobox value={languages} onChange={setLanguages} />
          </div>
        </div>
      </div>
    </Panel>
  );
}
